import {Order, ProductItem} from 'coreStore/data/orders/types';
import type {GiftCertificateItem as BasketGiftCertificateItem} from 'coreStore/data/basket/types';
import AddressModel from './AddressModel';
import ShipmentModel from './ShipmentModel';
import ProductItemModel from './ProductItemModel';
import GiftCertificateItemModel from './GiftCertificateItemModel';
import PaymentInstrumentModel from './PaymentInstrumentModel';
import ManagerRepository from 'coreClasses/ManagerRepository';

import {EConfirmationStatus, EOverallOrderStatus, EShippingStatus, EStatus} from './types';

/**
 * Represents an order
 */
class OrderModel {
	private managerRepository: ManagerRepository;
	private order: Order;
	private readonly shipments: Array<ShipmentModel>;
	private readonly productItems: Array<ProductItemModel>;
	private readonly giftCertificateItems: Array<GiftCertificateItemModel>;
	private readonly paymentInstruments: Array<PaymentInstrumentModel>;
	private readonly originalPaymentInstruments: Array<PaymentInstrumentModel>;
	private readonly billingAddress: AddressModel;

	/**
	 * @constructor
	 * @param {ManagerRepository} managerRepository - ManagerRepository instance
	 * @param {Order} order - order
	 */
	constructor(managerRepository: ManagerRepository, order: Order) {
		this.managerRepository = managerRepository;
		this.order = order;

		const productItemModels = this.order?.productItems
			? this.order.productItems.map(
				(productItem: ProductItem) =>
					new ProductItemModel(this.managerRepository, productItem, order.currency),
			  )
			: [];

		this.setupBundleKits(productItemModels);

		this.productItems = productItemModels.filter((item) => item.isVisible());

		this.shipments = this.order?.shipments?.map((shipment) => new ShipmentModel(shipment));
		this.paymentInstruments = this.order?.paymentInstruments?.map((paymentInstrument) => {
			let tempPaymentInstrument = Object.assign({}, paymentInstrument);

			if (this.order?.c_paymentsWithGiftCertificate?.length > 0) {
				const customPaymentsWithGiftCertificate = this.order?.c_paymentsWithGiftCertificate.find(
					(paymentWithGiftCertificate) =>
						paymentWithGiftCertificate.paymentInstrumentId === paymentInstrument.paymentInstrumentId,
				);

				if (customPaymentsWithGiftCertificate) {
					tempPaymentInstrument = Object.assign(tempPaymentInstrument, customPaymentsWithGiftCertificate);
				}
			}

			return new PaymentInstrumentModel(tempPaymentInstrument);
		});
		this.originalPaymentInstruments = this.order?.c_originalPaymentInstruments?.map(
			(paymentInstrument) => new PaymentInstrumentModel(paymentInstrument),
		);
		this.giftCertificateItems = this.order?.giftCertificateItems
			? this.order.giftCertificateItems?.map((productItem) =>
				new GiftCertificateItemModel(productItem as unknown as BasketGiftCertificateItem))
			: [];
		this.billingAddress = new AddressModel(this.order?.billingAddress);
	}

	/**
	 * Associates bundle kit components with their respective main bundle kit items.
	 *
	 * @param {ProductItemModel[]} productItems - The list of product items.
	 * @returns {void}
	 */
	private setupBundleKits(productItems: ProductItemModel[]): void {
		const bundleKits = new Map<string, ProductItemModel>();

		productItems.forEach((productItemModel) => {
			if (productItemModel.isParentBundleKit()) {
				// Main product in a bundle kit
				bundleKits.set(productItemModel.getParentBundleKitId(), productItemModel);
			} else if (productItemModel.isComponentBundleKit()) {
				// Component of a bundle kit
				const parentBundleId = productItemModel.getParentBundleKitId();
				const parentBundleKit = bundleKits.get(parentBundleId);

				if (parentBundleKit) {
					parentBundleKit.addBundleComponent(productItemModel);
				}
			}
		});
	}

	/**
	 * Returns true if order can be returned
	 * @returns {boolean} a boolean
	 */
	isReturnable(): boolean {
		return this.order.c_isReturnable;
	}

	/**
	 * Returns true if order is considered as recent
	 * @returns {boolean}
	 */
	isRecent(): boolean {
		return this.order.c_isRecent;
	}

	/**
	 * Returns the order number
	 * @returns {string} order number
	 */
	getOrderNumber(): string {
		return this.order.orderNo;
	}

	/**
	 * Returns order confirmation status
	 * @returns {string} confirmation status
	 */
	getConfirmationStatus(): string {
		return this.order.confirmationStatus;
	}

	/**
	 * Returns order creation date
	 * @returns {string} creation date
	 */
	getOrderCreationDate(): string {
		return this.order.creationDate;
	}

	/**
	 * Returns order currency
	 * @returns {string} currency
	 */
	getCurrency(): string {
		return this.order.currency;
	}

	/**
	 * Returns order product items
	 * @param {Boolean} splitByQuantity - split each product line item by quantity
	 * @returns {Array<ProductItemModel>} array of product items model
	 */
	getProductItems(splitByQuantity = false): Array<ProductItemModel> {
		if (!splitByQuantity) {
			return this.productItems;
		}

		let result = [];

		this.productItems.forEach((productItem) => {
			const originalQuantity = productItem.getQuantity();

			for (let i = 0, length = originalQuantity; i < length; ++i) {
				result.push(productItem);
			}
		});

		return result;
	}

	/**
	 * Returns order gift certificate items
	 * @returns {Array<GiftCertificateItemModel>} array of gift certificate item model
	 */
	getGiftCertificateItems(): Array<GiftCertificateItemModel> {
		return this.giftCertificateItems;
	}

	/**
	 * Returns an array containing both all product items and all gift certificate items in the order
	 * @returns {Array<ProductItemModel|GiftCertificateItemModel>}
	 */
	getAllItems(): Array<ProductItemModel | GiftCertificateItemModel> {
		const result = [];

		if (this.getProductItems()) {
			result.push(...this.getProductItems());
		}

		if (this.getGiftCertificateItems()) {
			result.push(...this.getGiftCertificateItems());
		}

		return result;
	}

	/**
	 * Returns order product item based on item id
	 * @param {String} itemId - the product item id
	 * @returns {ProductItemModel} product item model
	 */
	getProductItem(itemId): ProductItemModel {
		let result = this.getProductItems().filter((productItem) => productItem.getItemId() === itemId);

		return result.length ? result[0] : null;
	}

	/**
	 * Returns order shipments
	 * @returns {Array<ShipmentModel>} array of shipments model
	 */
	getShipments(): Array<ShipmentModel> {
		return this.shipments;
	}

	/**
	 * Returns order shipping status
	 * @returns {string} shipping status
	 */
	getShippingStatus(): string {
		return this.order.shippingStatus;
	}

	/**
	 * Returns order shipping status pickup
	 * @returns {string} shipping status
	 */
	getShippingStatusPickup(): string {
		return this.order.c_shippingStatusPickup;
	}

	/**
	 * Returns billing address model
	 * @returns {AddressModel} address model
	 */
	getBillingAddress(): AddressModel {
		return this.billingAddress;
	}

	/**
	 * Returns order payment instruments
	 * @returns {Array<PaymentInstrumentModel>} array of payment instruments
	 */
	getPaymentInstruments(): Array<PaymentInstrumentModel> {
		return this.paymentInstruments;
	}

	/**
	 * Returns original order payment instruments
	 * @returns {Array<PaymentInstrumentModel>} array of payment instruments
	 */
	getOriginalPaymentInstruments(): Array<PaymentInstrumentModel> {
		return this.originalPaymentInstruments;
	}

	/**
	 * Returns order status
	 * @returns {string} order status
	 */
	getStatus(): string {
		return this.order.status;
	}

	/**
	 * Returns order status
	 * @returns {string} order status
	 */
	getStatusToDisplay(): string {
		let displayStatus: string = this.getOverallStatus();
		const overallStatus = this.getOverallStatus();
		const shippingStatus = this.getShippingStatus();
		const shippingStatusPickup = this.getShippingStatusPickup();
		const status = this.getStatus();
		const confirmationStatus = this.getConfirmationStatus();

		switch (true) {
			case [
				EOverallOrderStatus.IN_PROGRESS,
				EOverallOrderStatus.RETURNED,
				EOverallOrderStatus.REFUNDED,
				EOverallOrderStatus.PARTLY_SHIPPED,
				EOverallOrderStatus.SHIPPED,
				EOverallOrderStatus.PARTLY_RETURNED,
				EOverallOrderStatus.READY_FOR_PICKUP,
				EOverallOrderStatus.READY_FOR_PARTIAL_PICKUP,
				EOverallOrderStatus.PICKED_UP,
				EOverallOrderStatus.PARTLY_PICKED_UP,
				EOverallOrderStatus.CANCELED,
			].includes(overallStatus as EOverallOrderStatus):
				return overallStatus;

			case [
				EShippingStatus.READY_FOR_PICKUP,
				EShippingStatus.READY_FOR_PARTIAL_PICKUP,
				EShippingStatus.PICKED_UP,
				EShippingStatus.PARTIALLY_PICKED_UP,
			].includes(shippingStatusPickup as EShippingStatus):
				return shippingStatusPickup;

			case ([EStatus.NEW, EStatus.OPEN].includes(status as EStatus) && shippingStatus === EShippingStatus.SHIPPED) ||
				status === EStatus.COMPLETED:
				return EShippingStatus.SHIPPED;

			case [EStatus.NEW, EStatus.OPEN].includes(status as EStatus) && confirmationStatus === EConfirmationStatus.CONFIRMED:
				return EConfirmationStatus.CONFIRMED;

			case [EStatus.NEW, EStatus.OPEN].includes(status as EStatus) &&
				confirmationStatus === EConfirmationStatus.NOT_CONFIRMED:
				return EStatus.IN_REVIEW;

			case [EStatus.NEW, EStatus.OPEN].includes(status as EStatus) && shippingStatus === EShippingStatus.NOT_SHIPPED:
				return EStatus.BEING_PROCESSED;

			case status === EStatus.CANCELLED:
				return status;

			default:
				return displayStatus;
		}
	}

	/**
	 * Returns order status
	 * @returns {string} order status
	 */
	getOverallStatus(): string {
		const order = this.order as Order & {c_overallStatus?: string};

		return (order.c_overallStatus || order.status).toLowerCase();
	}

	/**
	 * Returns order total
	 * @returns {number} order total
	 */
	getOrderTotal(): number {
		return this.order.orderTotal;
	}

	/**
	 * Returns product total
	 * @returns {number} product total
	 */
	getProductTotal(): number {
		return this.order.productTotal;
	}

	/**
	 * Returns merchandize total gross price
	 * @returns {number} total gross price (before promos applied)
	 */
	getMerchandizeTotalGrossPrice(): number {
		return this.order.merchandizeTotalGrossPrice;
	}

	/**
	 * Returns tracking image url
	 * @returns {string} tracking image url
	 */
	getTrackingImageURL(): string {
		return this.order.c_trackingImageURL;
	}

	/**
	 * Returns tracking url
	 * @returns {string} tracking url
	 */
	getTrackingUrl(): string {
		return this.order.c_trackingURL;
	}

	/**
	 * Returns return tracking url
	 * @returns {string} return tracking url
	 */
	getReturnTrackingUrl(): string {
		return this.order.c_returnTrackingURL;
	}

	/**
	 * Returns tracking number
	 * @returns {string} return tracking number
	 */
	getTrackingNumber(): string {
		return this.order.c_trackingNumber;
	}

	/**
	 * Returns return tracking number
	 * @returns {string} return return tracking number
	 */
	getReturnTrackingNumber(): string {
		return this.order.c_returnTrackingNumber;
	}

	/**
	 * Returns return authorization status
	 * @returns {string} return authorization status
	 */
	getReturnAuthorizationStatus(): string {
		return this.order.c_returnAuthorizationStatus;
	}

	/**
	 * Returns order
	 * @returns {Order} order
	 */
	getOrder(): Order {
		return this.order;
	}

	/**
	 * Returns estimated delivery date
	 * @returns {string} estimated delivery date
	 */
	getEstimatedDeliveryDate(): string {
		return this.order.c_estimatedDeliveryDate;
	}

	/**
	 * Returns return link
	 * @returns {string} return link
	 */
	getReturnLink(): string {
		return this.order.c_returnLink || '';
	}

	/**
	 * Returns order token
	 * @returns {string} order token
	 */
	getOrderToken(): string {
		return this.order.orderToken;
	}

	/**
	 * Returns the return fee for the order, mirroring ReturnAuthorizationRefundsCalculator on SFCC:
	 * - country return fee of the default shipment's country
	 * - 0 if the order total exceeds the country return order amount threshold
	 * - 0 if the customer belongs to any of the excluded customer groups
	 * Returns 0 when the country return configuration is not available.
	 * @returns {number} return fee
	 */
	getReturnFee(): number {
		const countryCode = this.shipments?.[0]?.getShippingAddress()?.getCountryCode();
		const country = countryCode && this.managerRepository.getCountriesMgr().getCountryByCode(countryCode);
		const returnFee = country?.getReturnFee() || 0;

		if (returnFee <= 0) {
			return 0;
		}

		const threshold = country.getReturnOrderAmountThreshold();

		if (threshold && this.getOrderTotal() > threshold) {
			return 0;
		}

		const groupsToExclude = country.getReturnAuthCustomerGroupsToExclude();
		const customerGroups = this.managerRepository
			.getCustomerMgr()
			.getCurrentCustomer()
			?.getCustomerGroups() || [];

		if (customerGroups.some((customerGroup) => groupsToExclude.includes(customerGroup.ID))) {
			return 0;
		}

		return returnFee;
	}
}

export default OrderModel;
